<template>
  <v-container fluid class="pa-4 rtl">
    <!-- Header -->
    <v-card class="header-card mb-4" elevation="2">
      <div class="d-flex align-center justify-space-between flex-wrap ga-3">
        <div>
          <div class="text-h5 font-weight-bold" style="color: #4a4f55">
            المساحات
          </div>
          <div class="text-body-2 text-grey-darken-1 mt-1">
            التوفر بحسب نوع المساحة، التشغيل، السلامة، الإضاءة والتهوية
          </div>
        </div>
      </div>
    </v-card>

    <!-- KPIs -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="2">
        <v-card class="kpi-card primary">
          <div class="kpi-value">{{ totalSpaces }}</div>
          <div class="kpi-label">إجمالي المساحات</div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="2">
        <v-card class="kpi-card success">
          <div class="kpi-value">{{ availableSpaces }}</div>
          <div class="kpi-label">متاحة / موجودة</div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="2">
        <v-card class="kpi-card warning">
          <div class="kpi-value">{{ fullWorkingSpaces }}</div>
          <div class="kpi-label">تعمل كامل</div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card danger">
          <div class="kpi-value">{{ partialWorkingSpaces }}</div>
          <div class="kpi-label">تعمل بشكل جزئي</div>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card info">
          <div class="kpi-value">{{ safeSpaces }}</div>
          <div class="kpi-label">مساحات آمنة</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Charts -->
    <v-row class="mb-4">
      <v-col cols="12" md="6">
        <v-card class="chart-card">
          <div class="table-header">التوفر بحسب نوع المساحة</div>
          <apexchart
            v-if="availabilitySeries.length"
            type="donut"
            height="320"
            :series="availabilitySeries"
            :options="donutOptions(availabilityLabels)"
          />
          <div v-else class="empty-chart">لا توجد بيانات</div>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="chart-card">
          <div class="table-header">أسباب التشغيل الجزئي</div>
          <apexchart
            v-if="partialIssuesSeries?.[0]?.data?.length"
            type="bar"
            height="320"
            :series="partialIssuesSeries"
            :options="barOptions(partialIssuesLabels)"
          />
          <div v-else class="empty-chart">لا توجد بيانات</div>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="chart-card">
          <div class="table-header">السلامة</div>
          <apexchart
            v-if="safetySeries.length"
            type="donut"
            height="320"
            :series="safetySeries"
            :options="donutOptions(safetyLabels)"
          />
          <div v-else class="empty-chart">لا توجد بيانات</div>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="chart-card">
          <div class="table-header">الإضاءة والتهوية</div>
          <apexchart
            v-if="lightingVentilationSeries?.[0]?.data?.length"
            type="bar"
            height="320"
            :series="lightingVentilationSeries"
            :options="barOptions(spaceLabels)"
          />
          <div v-else class="empty-chart">لا توجد بيانات</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-card class="mb-4 pa-4 filter-card" elevation="1">
      <v-row>
        <v-col cols="12" md="4">
          <v-text-field
            v-model="filters.search"
            label="بحث باسم المساحة"
            density="compact"
            variant="outlined"
            clearable
            hide-details
          />
        </v-col>

        <v-col cols="12" md="4">
          <v-select
            v-model="filters.operStatus"
            :items="operStatusItems"
            label="الحالة التشغيلية"
            density="compact"
            variant="outlined"
            clearable
            hide-details
          />
        </v-col>

        <v-col cols="12" md="4">
          <v-select
            v-model="filters.availability"
            :items="availabilityItems"
            label="التوفر"
            density="compact"
            variant="outlined"
            clearable
            hide-details
          />
        </v-col>
      </v-row>
    </v-card>

    <!-- Table -->
    <v-card elevation="1" class="table-card">
      <div class="table-header">
        تفاصيل المساحات الوظيفية
        <span>({{ filteredSpaces.length }})</span>
      </div>

      <v-table class="custom-table">
        <thead>
          <tr>
            <th>المساحة / المرفق</th>
            <th>متاحة</th>
            <th>الحالة التشغيلية</th>
            <th>أسباب التشغيل الجزئي</th>
            <th>السلامة</th>
            <th>الإضاءة</th>
            <th>التهوية</th>
            <th>الخصوصية</th>
            <th>عدد الأسرة</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="item in paginatedSpaces" :key="item.id">
            <td class="font-weight-bold">
              {{ item.space_code || "غير محدد" }}
            </td>

            <td>
              <v-chip
                :color="chipColor(item.is_available)"
                size="small"
                variant="flat"
              >
                {{ item.is_available || "غير محدد" }}
              </v-chip>
            </td>

            <td>
              <v-chip
                :color="chipColor(item.oper_status)"
                size="small"
                variant="flat"
              >
                {{ item.oper_status || "غير محدد" }}
              </v-chip>
            </td>

            <td>{{ item.partial_issues || "غير محدد" }}</td>

            <td>
              <v-chip
                :color="chipColor(item.safety)"
                size="small"
                variant="flat"
              >
                {{ item.safety || "غير محدد" }}
              </v-chip>
            </td>

            <td>
              <v-chip
                :color="chipColor(item.lighting)"
                size="small"
                variant="flat"
              >
                {{ item.lighting || "غير محدد" }}
              </v-chip>
            </td>

            <td>
              <v-chip
                :color="chipColor(item.ventilation)"
                size="small"
                variant="flat"
              >
                {{ item.ventilation || "غير محدد" }}
              </v-chip>
            </td>

            <td>
              <v-chip
                :color="chipColor(item.privacy)"
                size="small"
                variant="flat"
              >
                {{ item.privacy || "غير محدد" }}
              </v-chip>
            </td>

            <td>{{ item.beds_count ?? "—" }}</td>
          </tr>

          <tr v-if="!paginatedSpaces.length">
            <td colspan="9" class="text-center py-6">لا توجد بيانات مطابقة</td>
          </tr>
        </tbody>
      </v-table>

      <div
        class="d-flex align-center justify-space-between pa-4 flex-wrap ga-3"
      >
        <div>
          عرض {{ paginatedSpaces.length }} من {{ filteredSpaces.length }}
        </div>

        <div class="d-flex align-center">
          <v-select
            v-model="itemsPerPage"
            :items="[5, 10, 20, 50]"
            label="الصفوف"
            density="compact"
            variant="outlined"
            hide-details
            style="width: 120px"
          />

          <v-pagination
            v-model="currentPage"
            :length="pageCount"
            total-visible="7"
            class="ms-4"
          />
        </div>
      </div>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import api from "@/plugins/api";

const spaces = ref([]);
const loading = ref(false);

const filters = ref({
  search: "",
  operStatus: null,
  availability: null,
});

const currentPage = ref(1);
const itemsPerPage = ref(10);

const fetchSpaces = async () => {
  try {
    loading.value = true;

    const { data } = await api.get("/functional-spaces");

    spaces.value = Array.isArray(data)
      ? data
      : Array.isArray(data?.data)
      ? data.data
      : [];
  } catch (error) {
    console.error("Functional Spaces API Error:", error);
    spaces.value = [];
  } finally {
    loading.value = false;
  }
};

onMounted(fetchSpaces);

const totalSpaces = computed(() => spaces.value.length);

const availableSpaces = computed(
  () => spaces.value.filter((item) => item.is_available === "نعم").length
);

const fullWorkingSpaces = computed(
  () => spaces.value.filter((item) => item.oper_status === "كامل").length
);

const partialWorkingSpaces = computed(
  () => spaces.value.filter((item) => item.oper_status === "جزئي").length
);

const safeSpaces = computed(
  () =>
    spaces.value.filter((item) =>
      ["كامل", "آمن", "متوسط"].includes(item.safety)
    ).length
);

const countBy = (field) => {
  const result = {};

  spaces.value.forEach((item) => {
    const value = item[field] || "غير محدد";
    result[value] = (result[value] || 0) + 1;
  });

  return result;
};

const availabilityByType = computed(() => countBy("is_available"));
const partialIssues = computed(() => countBy("partial_issues"));
const safetyData = computed(() => countBy("safety"));

const availabilityLabels = computed(() =>
  Object.keys(availabilityByType.value)
);
const availabilitySeries = computed(() =>
  Object.values(availabilityByType.value)
);

const partialIssuesLabels = computed(() => Object.keys(partialIssues.value));
const partialIssuesSeries = computed(() => [
  {
    name: "عدد المساحات",
    data: Object.values(partialIssues.value),
  },
]);

const safetyLabels = computed(() => Object.keys(safetyData.value));
const safetySeries = computed(() => Object.values(safetyData.value));

const spaceLabels = computed(() =>
  spaces.value.map((item) => item.space_code || "غير محدد")
);

const scoreValue = (value) => {
  if (value === "كامل" || value === "جيد") return 100;
  if (value === "متوسط") return 60;
  if (value === "جزئي") return 40;
  return 0;
};

const lightingVentilationSeries = computed(() => [
  {
    name: "الإضاءة",
    data: spaces.value.map((item) => scoreValue(item.lighting)),
  },
  {
    name: "التهوية",
    data: spaces.value.map((item) => scoreValue(item.ventilation)),
  },
]);

const donutOptions = (labels) => ({
  labels,
  chart: {
    type: "donut",
  },
  legend: {
    position: "bottom",
  },
  dataLabels: {
    enabled: true,
  },
});

const barOptions = (labels) => ({
  chart: {
    type: "bar",
    toolbar: {
      show: false,
    },
  },
  xaxis: {
    categories: labels,
    labels: {
      rotate: -45,
    },
  },
  plotOptions: {
    bar: {
      borderRadius: 6,
    },
  },
});

const chipColor = (value) => {
  if (!value) return "grey";

  if (["كامل", "نعم", "آمن", "جيد"].includes(value)) return "green";
  if (["جزئي", "متوسط"].includes(value)) return "orange";
  if (["لا", "غير آمن", "سيئ", "غير متاح"].includes(value)) return "red";

  return "blue-grey";
};

const operStatusItems = computed(() => [
  ...new Set(spaces.value.map((item) => item.oper_status).filter(Boolean)),
]);

const availabilityItems = computed(() => [
  ...new Set(spaces.value.map((item) => item.is_available).filter(Boolean)),
]);

const filteredSpaces = computed(() => {
  return spaces.value.filter((item) => {
    const search = filters.value.search?.trim()?.toLowerCase();

    if (search && !(item.space_code || "").toLowerCase().includes(search)) {
      return false;
    }

    if (
      filters.value.operStatus &&
      item.oper_status !== filters.value.operStatus
    ) {
      return false;
    }

    if (
      filters.value.availability &&
      item.is_available !== filters.value.availability
    ) {
      return false;
    }

    return true;
  });
});

const pageCount = computed(() => {
  return Math.max(
    1,
    Math.ceil(filteredSpaces.value.length / itemsPerPage.value)
  );
});

const paginatedSpaces = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  return filteredSpaces.value.slice(start, start + itemsPerPage.value);
});

watch(
  () => [
    filters.value.search,
    filters.value.operStatus,
    filters.value.availability,
    itemsPerPage.value,
  ],
  () => {
    currentPage.value = 1;
  }
);
</script>

<style scoped>
.rtl {
  direction: rtl;
}

.header-card {
  border-right: 5px solid #4a4f55;
  border-radius: 18px;
  padding: 20px;
}

.kpi-card {
  border-radius: 18px;
  padding: 20px;
  text-align: center;
  min-height: 120px;
}

.kpi-value {
  font-size: 34px;
  font-weight: 800;
}

.kpi-label {
  margin-top: 6px;
  font-size: 13px;
}

.primary {
  background: #f0f1f2;
  color: #4a4f55;
}

.danger {
  background: #fdecea;
  color: #c0392b;
}

.warning {
  background: #fff4e5;
  color: #d4821a;
}

.success {
  background: #eafaf1;
  color: #27ae60;
}

.info {
  background: #e8f4fd;
  color: #1976d2;
}

.chart-card,
.table-card,
.filter-card {
  border-radius: 18px;
  overflow: hidden;
}

.table-header {
  padding: 18px;
  font-size: 18px;
  font-weight: 700;
  color: #3a3f47;
  border-bottom: 1px solid #eee;
}

.table-header span {
  color: #6b7280;
  font-size: 14px;
}

.empty-chart {
  height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #777;
}

.custom-table {
  direction: rtl;
}

:deep(th) {
  font-weight: 800 !important;
  color: #3a3f47 !important;
  white-space: nowrap;
}

:deep(td) {
  font-size: 14px;
  white-space: nowrap;
}
</style>
